import {partition} from '@benchling/js-utils';
import Backbone, {ModelSetOptions} from 'backbone';
import {Queue, queue} from 'd3-queue';
import $ from 'jquery';
import React from 'react';
import ReactDOMClient from 'react-dom/client';
import ReactDOMServer from 'react-dom/server';
import _ from 'underscore';

import * as OnboardingActions from 'actions/OnboardingActions';
import * as analytics from 'analytics/Analytics';
import {NucleotideType} from 'biojs/BioConstants';
import {Model as BioJSSequence} from 'biojs/Sequence';
import Tag from 'components/Tag';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {MinimalFolder, NumberIDFolder} from 'constants/FolderConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import TagDropdown from 'import-sequence/backbone/TagDropdown';
import TagSequence from 'import-sequence/backbone/TagSequence';
import navigateToURL from 'lib/navigateToURL';
import FeatureLibraryModal from 'molbio/feature-libraries/FeatureLibraryModal';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import * as SequenceActions from 'molbio/SequenceActions';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as RegistryUtil from 'registry/RegistryUtil';
import {StudySearchItem} from 'studies/StudySearchInput';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import DragDropUpload from 'util/DragDropUpload';
import {splitExtension} from 'util/FileUtil';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';
import {HTMLInputEvent} from 'util/Types';
import AddToWorklistModal from 'worklists/AddToWorklistModal';
import {getWorklistType} from 'worklists/WorklistUtil';

import ImportFromFile from './ImportFromFile';
import UploadingFile from './UploadingFile';
import UploadingSequence, {UploadingSequenceProps} from './UploadingSequence';

interface ImportTagDropdownOptions {
  model: Backbone.Collection<UploadFile>;
  el: JQuery;
}

export class ImportTagDropdown extends TagDropdown {
  override model!: Backbone.Collection<UploadFile>;

  constructor(options: ImportTagDropdownOptions) {
    super(options);
  }

  override getAffected(): Array<TagSequence> {
    return this.model
      .where({anySelected: true})
      .flatMap((file) => file.get('sequences').where({selected: true}));
  }
}

export enum UploadStatus {
  NOT_UPLOADED = 0,
  UPLOADING = 1,
  UPLOAD_SUCCESS = 2,
  UPLOAD_FAILURE = 3,
}

interface UploadFileAttrs {
  uploadStatus: UploadStatus;
  uploadPercent: number;
  file: File | null;
  selectable: boolean;
  anySelected: boolean;
  anyRNASelected: boolean;
  allSelected: boolean;
  sequences: Backbone.Collection<TagSequence>;
  response?: string;
}

interface UploadFile {
  get<K extends keyof UploadFileAttrs>(attribute: K): UploadFileAttrs[K];
  set(obj: Partial<UploadFileAttrs>, options?: ModelSetOptions): UploadFile;
  set<K extends keyof UploadFileAttrs>(
    attributeName: K,
    value: UploadFileAttrs[K],
    options?: ModelSetOptions
  ): UploadFile;
}

class UploadFile extends Backbone.Model {
  override defaults(): UploadFileAttrs {
    return {
      uploadStatus: UploadStatus.NOT_UPLOADED,
      uploadPercent: 0,
      file: null,
      selectable: false,
      anySelected: false,
      anyRNASelected: false,
      allSelected: false,
      sequences: new Backbone.Collection([], {model: TagSequence}),
      response: undefined,
    };
  }

  override initialize(): void {
    this.listenTo(this.get('sequences'), 'add remove change:selected', () => {
      const numSelected = this.get('sequences').where({selected: true, status: 'success'}).length;
      const numRNASelected = this.get('sequences')
        .where({
          selected: true,
          status: 'success',
        })
        .filter(
          (sequence: TagSequence) => sequence.get('_sequence').get('nucleotideType') === NucleotideType.RNA
        ).length;
      const allCount = this.get('sequences').where({status: 'success'}).length;
      this.set({
        anySelected: numSelected > 0,
        anyRNASelected: numRNASelected > 0,
        allSelected: numSelected === allCount && allCount > 0,
        selectable: allCount > 0,
      });
    });
    this.on('change:uploadStatus', () => {
      if (this.get('uploadStatus') === UploadStatus.UPLOAD_SUCCESS) {
        OnboardingActions.completeOnboardingTask(
          OnboardingConstants.MODULES.SEQUENCE,
          OnboardingConstants.IMPORT_SEQUENCE
        );
        const [successfulSeq, failedSeq] = partition(
          this.get('sequences').models,
          (seq: TagSequence) => seq.get('status') === 'success'
        );
        if (successfulSeq.length > 0) {
          analytics.track('sequence', 'import_success', {
            product: ANALYTICS_MODULE.MOLBIO,
            importFlow: DNA_SEQUENCE_IMPORT_FLOW.FILE,
            numSequences: successfulSeq.length,
          });
        }
        if (failedSeq.length > 0) {
          analytics.track('sequence', 'import_fail', {
            product: ANALYTICS_MODULE.MOLBIO,
            importFlow: DNA_SEQUENCE_IMPORT_FLOW.FILE,
            errors: failedSeq.map((sequence) => sequence.get('_sequence').get('message')),
          });
        }
      } else if (this.get('uploadStatus') === UploadStatus.UPLOAD_FAILURE) {
        analytics.track('sequence', 'import_fail', {
          product: ANALYTICS_MODULE.MOLBIO,
          importFlow: DNA_SEQUENCE_IMPORT_FLOW.FILE,
          errors: [this.get('response')],
        });
      }
    });
  }

  upload(
    libraryId: ImportUtil.LegacyFolderId,
    nucleotideType: NucleotideType,
    studySearchItems?: Array<StudySearchItem>
  ): JQueryPromise<ImportUtil.SequenceImportResponse> {
    let studyIds: Array<string> = [];
    if (studySearchItems) {
      studyIds = studySearchItems.map((studyItem) => studyItem.id);
    }
    this.set({uploadStatus: UploadStatus.UPLOADING});
    return ImportUtil.uploadSequence(this.get('file')!, libraryId, true, nucleotideType, studyIds)
      .progress((percent) => {
        // Never let it fall back down from re-tries.
        percent = Math.max(percent, this.get('uploadPercent'));
        this.set({uploadPercent: percent});
      })
      .fail((res) => {
        if (res.maxAttemptsExceeded) {
          this.set({
            response: GENERIC_ERROR_MESSAGE,
            uploadStatus: UploadStatus.UPLOAD_FAILURE,
          });
        } else {
          const response = net.isServerRequestError(res)
            ? res.responseText
            : notifications.getFriendlyError(res);
          this.set({
            response: response ?? GENERIC_ERROR_MESSAGE,
            uploadStatus: UploadStatus.UPLOAD_FAILURE,
          });
        }
      })
      .done(({results}: ImportUtil.SequenceImportResponse) => {
        // @ts-expect-error
        this.get('sequences').add(results, {parse: true});

        // Set this in two different operations so upload success handler is only called once
        this.set({uploadPercent: 100});
        this.set({uploadStatus: UploadStatus.UPLOAD_SUCCESS});
      });
  }
}

export {UploadFile};

interface SequenceRowOptions extends Backbone.ViewOptions<TagSequence> {
  closeModal: () => void;
}

export class SequenceRow extends Backbone.View<TagSequence> {
  closeModal!: () => void;

  static initClass(): void {
    this.prototype.className = 'sequence-row';
    this.prototype.events = {
      // @ts-expect-error change isn't in Backbone events
      'change input[type=checkbox]': 'toggleSelected',
      'click .close': 'handleDestroy',
      'click .sequence-name': function (e: JQueryEventObject) {
        return e.preventDefault();
      },
      'focus .sequence-name': 'handleNameFocus',
      'keydown .sequence-name': 'handleNameKeydown',
      'blur .sequence-name': 'handleNameBlur',
      'click .sequence-link': 'handleOpenSequence',
      'click .folder-name': 'handleOpenFolder',
    };
  }

  constructor(options: SequenceRowOptions) {
    super(options);
  }

  override initialize(options: SequenceRowOptions): void {
    this.closeModal = options.closeModal;
    this.listenTo(this.model, 'change:visible', (_: TagSequence, visible: boolean) => {
      this.renderVisible(visible);
    });
    this.listenTo(this.model, 'change:selected', (_: TagSequence, selected: boolean) => {
      this.renderSelect(selected);
    });
    this.listenTo(this.model.get('tags'), 'add remove change reset', () => {
      this.renderTags();
    });
    this.listenTo(this.model.get('tags'), 'add remove reset', () => this.model.trigger('tagChange'));
    this.listenTo(
      this.model.get('_sequence'),
      'change:folder',
      (model: BioJSSequence, folder: NumberIDFolder) => {
        this.renderFolderName(model, folder);
      }
    );
    this.listenTo(this.model.get('_sequence'), 'change:circular', (_: BioJSSequence, circular: boolean) => {
      this.renderCircular(circular);
    });
    this.listenTo(this.model.get('_sequence'), 'change:editURL', () => {
      this.renderLink();
    });
    this.listenTo(this.model.get('_sequence'), 'change:name', (_: BioJSSequence, name: string) => {
      this.model.trigger('sequenceChange');
      this.renderName(name);
    });
    this.listenTo(this.model.get('_sequence'), 'destroy', () => {
      this.handleDestroy();
    });
  }

  renderName(name: string): void {
    this.$el.find('.sequence-name').text(name);
  }

  renderVisible(visible: boolean): void {
    this.$el.toggleClass('hidden', !visible);
  }

  renderSelect(selected: boolean): void {
    this.$el.find('input[type=checkbox]').prop('checked', selected);
  }

  renderFolderName(model: BioJSSequence, folder: NumberIDFolder): void {
    this.$el.find('.folder-name').attr('href', model.folder()!.url).text(folder.name);
  }

  renderCircular(circular: boolean): void {
    this.$el.find('.sequence-topology').text(circular ? 'circular' : 'linear');
  }

  toggleSelected(e: JQueryEventObject): void {
    this.model.set({selected: $(e.target).prop('checked')});
  }

  handleDestroy(): void {
    this.model.collection.remove(this.model);
    this.remove();
  }

  handleNameFocus(e: JQueryEventObject): void {
    const $name = $(e.currentTarget);
    $name.data('originalName', $name.text());
  }

  handleNameKeydown(e: JQueryEventObject): void {
    const $name = $(e.currentTarget);
    if (e.which === 13) {
      e.preventDefault();
      if ($name.text().trim().length === 0) {
        return;
      }
      const newName = $name.text().trim();
      const sequence = this.model.get('_sequence');
      $name.data('originalName', newName);
      $name.blur();
      return sequence
        .save({name: newName}, {patch: true})
        .fail(notifications.notifyError)
        .done(() => {
          notifications.success(
            <>
              Sequence <strong>{newName}</strong> updated with new name.
            </>
          );
        });
    }
  }

  handleNameBlur(e: JQueryEventObject): void {
    const $name = $(e.currentTarget);
    $name.text($name.data('originalName'));
  }

  handleOpenSequence(e: JQueryEventObject): void {
    e.preventDefault();
    FileBrowserActions.openFile(this.model.id);
    this.closeModal();
  }

  handleOpenFolder(e: JQueryEventObject): void {
    e.preventDefault();
    navigateToURL(this.model.get('_sequence').folder()!.url);
    this.closeModal();
  }

  renderTags(): void {
    const $els = this.model.get('tags').models.map((tag: Tag) => {
      const $el = $('<span>')
        .addClass('file-tag')
        .append($('<span>').text(tag.get('name')));
      if (tag.get('value') != null) {
        $el.append($('<span>').text(tag.get('value')));
      }
      return $el;
    });
    this.$el.find('.tags').html($els);
  }

  renderLink(): void {
    this.$el.find('.sequence-link').attr('href', this.model.get('_sequence').get('editURL'));
  }

  override render(): SequenceRow {
    const data = this.model.get('_sequence').toJSON();
    this.$el.html(
      // TODO: Should this be `ReduxDOMRender.renderToStaticMarkup` instead?
      ReactDOMServer.renderToStaticMarkup(<UploadingSequence {...(data as UploadingSequenceProps)} />)
    );
    this.renderTags();
    this.renderLink();
    this.$el.find('input').prop('checked', this.model.get('selected'));
    return this;
  }
}

SequenceRow.initClass();

interface UploadFileViewOptions extends Backbone.ViewOptions<UploadFile> {
  closeModal: () => void;
}

class UploadFileView extends Backbone.View<UploadFile> {
  closeModal!: () => void;
  sequenceRows!: Array<SequenceRow>;

  static initClass(): void {
    this.prototype.tagName = 'li';
    this.prototype.className = 'upload-sequence';
  }

  override events(): Backbone.EventsHash {
    return {
      'change .file-row input[type=checkbox]': 'toggleSelected',
      'click .error-row .close': 'handleRemove',
    };
  }

  constructor(options: UploadFileViewOptions) {
    super(options);
  }

  override initialize({closeModal}: UploadFileViewOptions): void {
    this.closeModal = closeModal;
    this.listenTo(this.model, 'change:uploadStatus change:uploadPercent', () => {
      this.renderProgress();
    });
    this.listenTo(this.model, 'destroy', () => {
      this.remove();
    });
    this.listenTo(this.model, 'change:anySelected change:allSelected', () => {
      this.renderSelect();
    });
    this.listenTo(this.model.get('sequences'), 'change:visible', () => {
      this.toggleVisible();
    });
    this.listenTo(this.model.get('sequences'), 'remove', () => {
      this.handleRemove();
    });
    this.listenTo(this.model.get('sequences'), 'add remove tagChange sequenceChange', () => {
      this.model.trigger('tagChange sequenceChange');
    });
    this.sequenceRows = [];
  }

  toggleVisible(): void {
    const visible = this.model.get('sequences').where({visible: true}).length > 0;
    this.$el.toggleClass('hidden', !visible);
  }

  toggleSelected(e: JQueryEventObject): void {
    const selected = $(e.target).prop('checked');
    this.model
      .get('sequences')
      .where({status: 'success'})
      .map((sequence: TagSequence) => sequence.set({selected}));
  }

  handleRemove(): void {
    if (this.model.get('sequences').length === 0) {
      this.model.destroy();
    } else {
      this.renderProgress();
    }
  }

  renderSelect(): void {
    const numSelected = this.model.get('sequences').where({selected: true, status: 'success'}).length;
    const numTotal = this.model.get('sequences').where({status: 'success'}).length;
    this.$el.find('.file-row input[type=checkbox]').prop({
      checked: numSelected > 0,
      indeterminate: numSelected < numTotal && numSelected > 0,
      disabled: numTotal === 0,
    });
  }

  renderProgress(): void {
    let view;
    const progressEl = this.$el.find('.progress-bar').text('');
    this.renderSelect();
    this.$el
      .find('.error-row')
      .toggleClass('hidden', this.model.get('uploadStatus') !== UploadStatus.UPLOAD_FAILURE);
    progressEl.removeClass('progress-bar-success progress-bar-danger progress-bar-warning');
    switch (this.model.get('uploadStatus')) {
      case UploadStatus.NOT_UPLOADED:
        progressEl.css('width', '0%');
        break;
      case UploadStatus.UPLOADING:
        progressEl.css({width: `${this.model.get('uploadPercent')}%`});
        progressEl.parent().addClass('progress-striped active');
        this.$el.find('.progress-percentage-text').text(`${Math.round(this.model.get('uploadPercent'))}%`);
        break;
      case UploadStatus.UPLOAD_SUCCESS:
        progressEl.css('width', '100%');
        progressEl.parent().removeClass('progress-striped active');
        if (this.model.get('sequences').where({status: 'error'}).length === 0) {
          progressEl.addClass('progress-bar-success').text('UPLOAD DONE');
        } else if (this.model.get('sequences').where({status: 'success'}).length === 0) {
          progressEl.addClass('progress-bar-danger').text('UPLOAD FAILED');
        } else {
          progressEl.addClass('progress-bar-warning').text('DONE, WITH ERRORS');
        }
        for (view of this.sequenceRows) {
          view.remove();
        }
        this.sequenceRows = this.model.get('sequences').map((sequence: TagSequence) =>
          new SequenceRow({
            model: sequence,
            closeModal: this.closeModal,
          }).render()
        );
        this.$el.find('.file-row').after(this.sequenceRows.map((view) => view.el));
        this.$el.find('.progress-percentage-text').text('');
        break;
      case UploadStatus.UPLOAD_FAILURE:
        progressEl.css('width', '100%').addClass('progress-bar-danger').text('ERROR UPLOADING');
        progressEl.parent().removeClass('progress-striped active');
        this.$el.find('.error-row .message').text(this.model.get('response')!);
        this.$el.find('.progress-percentage-text').text('');
        break;
      default:
        break;
    }
  }

  override render(): UploadFileView {
    this.$el.html(
      ReactDOMServer.renderToStaticMarkup(<UploadingFile filename={this.model.get('file')!.name} />)
    );
    this.renderProgress();
    return this;
  }
}

UploadFileView.initClass();

interface ImportFromFileViewOptions extends Backbone.ViewOptions<Backbone.Model> {
  closeModal: () => void;
  targetFolderId?: string;
  uploadFiles?: Backbone.Collection<UploadFile>;
}

class ImportFromFileView extends Backbone.View<Backbone.Model> {
  closeModal!: () => void;
  nucleotideType!: NucleotideType;
  targetFolderId?: string;
  uploadQueue!: Queue;
  uploadFiles!: Backbone.Collection<UploadFile>;
  override el!: HTMLElement;
  tagDropdown?: ImportTagDropdown;
  studySearchItems?: Array<StudySearchItem>;
  reactRoot: ReactDOMClient.Root | null = null;

  override events(): Backbone.EventsHash {
    return {
      'click .select-all': 'toggleSelected',
      'click .file-choose': 'chooseFile',
      'click .move-to-button': 'handleMove',
      'click .topology-group a': 'updateTopology',
      'click .auto-annotate-button': 'autoAnnotate',
      'click .add-to-worklist-button': 'addToWorklist',
      'click .delete-button': 'deleteSequences',
      'click .tag-button': 'focusTagSearch',
      'change #sequence-upload': 'addFromChooser',
      filedrop: 'handleDrop',
      'change .errors-only input': 'toggleShowErrors',
      'click .open-button': 'handleOpenSelectedSequences',
      'click .js-renameGenBank': 'renameGenBankFiles',
    };
  }

  constructor(options: ImportFromFileViewOptions) {
    super(options);
  }

  override initialize({closeModal, targetFolderId, uploadFiles}: ImportFromFileViewOptions): void {
    this.closeModal = closeModal;
    this.nucleotideType = NucleotideType.DNA;
    this.targetFolderId = targetFolderId;
    this.uploadQueue = queue(6);
    this.uploadFiles = uploadFiles ?? new Backbone.Collection();
    this.listenTo(this.uploadFiles, 'add remove tagChange', () => {
      this.renderTagChoices();
    });
    this.listenTo(this.uploadFiles, 'add remove sequenceChange', () => {
      this.renderGenBankWarning();
    });
    this.listenTo(
      this.uploadFiles,
      'add remove change:uploadStatus change:anySelected change:allSelected',
      () => {
        this.renderSelect();
      }
    );
    this.listenTo(this.uploadFiles, 'add remove change:anySelected change:allSelected', () => {
      this.renderUploadFilesHelp();
    });
    this.listenTo(this.uploadFiles, 'change:anyRNASelected', () => {
      this.renderTopologySelect();
    });

    DragDropUpload.push(this.el);
  }

  getSequencesAndNamesFromSinglePartGenBankFiles(): Array<{name: string; sequence: TagSequence}> {
    // Keep in sync with benchling/pybio/seqio/adapters/genbank.py
    const GB_EXTS = ['.gb', '.gbk', '.genbank'];
    const sequenceAndNames = [];
    for (const uploadFile of this.uploadFiles.models) {
      const {root, extension} = splitExtension(uploadFile.get('file')!.name);
      if (!GB_EXTS.includes(extension.toLowerCase()) || uploadFile.get('sequences').length !== 1) {
        continue;
      }

      const sequence = uploadFile.get('sequences').at(0);

      // If the filename matches the sequence name already, skip it.
      if (sequence.get('_sequence').get('name') === root) {
        continue;
      }
      // If the sequence file is broken, skip it.
      if (sequence.get('status') !== 'success') {
        continue;
      }

      sequenceAndNames.push({name: root, sequence});
    }
    return sequenceAndNames;
  }

  renderGenBankWarning(): void {
    const hideWarning = this.getSequencesAndNamesFromSinglePartGenBankFiles().length === 0;
    this.$('.js-genBankFilenameWarning').toggleClass('hidden', hideWarning);
  }

  renameGenBankFiles(): void {
    const sequencesAndNames = this.getSequencesAndNamesFromSinglePartGenBankFiles();
    const sequences = sequencesAndNames.map(({sequence}) => sequence);
    const names = sequencesAndNames.map(({name}) => name);
    net
      .patchJSON({
        url: '/1/api/sequences',
        data: {
          // eslint-disable-next-line no-restricted-syntax
          sequence_ids: _.pluck(sequences, 'id'),
          names,
        },
      })
      .done(() => {
        for (const {sequence, name} of sequencesAndNames) {
          sequence.get('_sequence').set('name', name);
          FolderDAOCache.invalidateFolderContents(sequence.get('_sequence').folder().api_identifier);
        }
        notifications.success(`Successfully updated ${sequencesAndNames.length} sequence(s).`);
      })
      .fail(notifications.notifyError);
  }

  firstUploadedFile(): TagSequence | null {
    for (const file of this.uploadFiles.models) {
      for (const sequence of file.get('sequences').models) {
        if (sequence.id) {
          return sequence;
        }
      }
    }
    return null;
  }

  toggleShowErrors(e: JQueryEventObject): void {
    const showErrorsOnly = $(e.currentTarget).prop('checked');
    this._handleShowErrors(showErrorsOnly);
  }

  _handleShowErrors(showErrorsOnly: boolean): void {
    this.$el.find('.select-all').prop('disabled', showErrorsOnly);
    this.$el.find('.btn-group .btn').toggleClass('disabled', showErrorsOnly);
    this.uploadFiles.models.forEach((file) =>
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      file
        .get('sequences')
        .models.forEach((sequence: TagSequence) =>
          sequence.set('visible', sequence.get('status') === 'error' || !showErrorsOnly)
        )
    );
  }

  toggleSelected(e: JQueryEventObject): void {
    const selected = $(e.target).prop('checked');
    const toggleQueue = queue(1);
    const uploadFiles = this.uploadFiles.where({selectable: true});
    _.defer(() =>
      uploadFiles.map((uploadFile) => {
        const result = [];
        for (const sequence of uploadFile.get('sequences').where({status: 'success'})) {
          result.push(
            toggleQueue.defer((sequence: TagSequence, callback: () => unknown): void => {
              sequence.set({selected});
              _.defer(() => callback());
            }, sequence)
          );
        }
        return result;
      })
    );
  }

  renderTopologySelect(): void {
    const anyRNASelected = this.uploadFiles.where({anyRNASelected: true}).length > 0;
    const circularTopologyOption = this.$el.find('.topology-option-circular');
    circularTopologyOption.toggleClass('disabled', anyRNASelected);
    const circularTopologyAnchor = circularTopologyOption.find('a');
    if (anyRNASelected) {
      circularTopologyAnchor.text('Circular RNA is not currently supported');
    } else {
      circularTopologyAnchor.text('Circular');
    }
  }

  focusTagSearch(): void {
    _.defer(() => {
      this.tagDropdown!.open();
    });
  }

  selectedSequences(): Array<TagSequence> {
    return this.uploadFiles
      .chain()
      .map((file: UploadFile) => file.get('sequences').where({selected: true}))
      .flatten()
      .value();
  }

  openMoveModal(folders: Array<MinimalFolder>): void {
    FolderSelectorModalClass.open({
      initialFolderId: this.targetFolderId,
      showInventory: true,
      submitText: 'Move',
      title: 'Move to folder',
      folders,
      addFolder: (folder) => {
        this.openMoveModal(FolderSelectorModalClass.withFolderAdded(folders, folder));
      },
      onChange: (e) => {
        const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
        this.updateLibrary(_.findWhere(folders, {api_identifier: folderId})!);
        FolderSelectorModalClass.close();
      },
    });
  }

  handleMove(e: JQueryEventObject): void {
    e.preventDefault();
    FolderSelectorModalClass.fetchFolders({level: 'append'}, this.openMoveModal.bind(this));
  }

  updateLibrary(folder: MinimalFolder): void {
    const sequences = this.selectedSequences();
    const parentFolderIds = sequences.map((sequence) => sequence.get('_sequence').folder().api_identifier);
    FileBrowserDAO.moveFiles(
      sequences.map(({id}) => id),
      folder.api_identifier,
      {parentFolderIds}
    )
      .done(() => {
        notifications.success(`Successfully moved ${sequences.length} sequence(s) to folder.`);
        // The sequence_urls are still wrong, but we redirect so it's okay.
        for (const sequence of sequences) {
          sequence.get('_sequence').set({
            folder,
            owner: folder.owner_handle,
          });
        }
      })
      .fail(() => {
        notifications.error('An error occurred while updating sequences.');
      });
  }

  updateTopology(e: JQueryEventObject): void {
    e.preventDefault();
    const circular = $(e.currentTarget).data('circular') === 1;
    const sequences = this.selectedSequences();
    const anyRNASelected = this.uploadFiles.where({anyRNASelected: true}).length > 0;
    if (!anyRNASelected || !circular) {
      net
        .patchJSON({
          url: '/1/api/sequences',
          data: {
            // eslint-disable-next-line no-restricted-syntax
            sequence_ids: _.pluck(sequences, 'id'),
            circular,
          },
        })
        .done(() => {
          for (const sequence of sequences) {
            sequence.get('_sequence').set('circular', circular);
          }
          notifications.success(`Successfully updated ${sequences.length} sequence(s).`);
        })
        .fail(() => {
          notifications.error('An error occurred while updating sequences.');
        });
    }
  }

  async autoAnnotate(e: JQueryEventObject): Promise<void> {
    e.preventDefault();
    const sequences = this.selectedSequences();
    const featureLibraryIds = await legacyRunModal(FeatureLibraryModal, {});
    if (!featureLibraryIds) {
      return;
    }
    SequenceActions.batchAutoAnnotate(
      sequences.map((sequence) => sequence.get('id')),
      featureLibraryIds
    );
    for (const sequence of sequences) {
      analytics.track('annotation', 'clicked_auto_annotate', {sequence_id: sequence.get('id')});
    }
  }

  addToWorklist(e: JQueryEventObject): void {
    e.preventDefault();
    const sequenceIds = this.selectedSequences().map((sequence) => sequence.get('id'));
    legacyRunModal(AddToWorklistModal, {
      ids: sequenceIds,
      worklistType: getWorklistType(sequenceIds),
      refreshOptions: {
        refreshWorklistItemsSearch: true,
        refreshWorklistsSearch: true,
        refreshRecentWorklists: true,
      },
    });
  }

  deleteSequences(): void {
    $('.delete-button').addClass('disabled');
    const sequences = this.selectedSequences();
    const parentFolderIds = sequences.map((sequence) => sequence.get('_sequence').folder().api_identifier);
    FileBrowserDAO.forceDeleteFiles(
      sequences.map(({id}) => id),
      {parentFolderIds}
    )
      .done(() => {
        for (const sequence of sequences) {
          sequence.collection.remove(sequence);
        }
        $('.delete-button').removeClass('disabled');
        notifications.success(`Successfully deleted ${sequences.length} sequence(s).`);
      })
      .fail(() => {
        notifications.error('An error occurred while deleting sequences.');
      });
  }

  didRejectFile(): boolean {
    if (!this.targetFolderId) {
      notifications.warn('In order to upload a file, select a destination folder first.', {
        id: 'importview-chooseFile',
      });
      return true;
    }
    return false;
  }

  chooseFile(): void {
    if (this.didRejectFile()) {
      return;
    }
    this.$el.find('#sequence-upload').click();
  }

  addFromChooser(e: HTMLInputEvent): void {
    this.addFiles(Array.from(e.target.files!));
    // Reset the file input (needs to be via the form) so that
    // user can re-select the same file and trigger a change.
    ($(e.currentTarget!).closest('form')[0] as HTMLFormElement).reset();
  }

  handleDrop(_: Backbone.Model, files: FileList): void {
    if (this.didRejectFile()) {
      return;
    }
    this.addFiles(Array.from(files));
  }

  handleOpenSelectedSequences(): void {
    const selectedSequenceIds = [];
    for (const uploadFile of this.uploadFiles.where({selectable: true})) {
      for (const sequence of uploadFile.get('sequences').where({status: 'success', selected: true})) {
        selectedSequenceIds.push(sequence.id);
      }
    }

    const fileStates = FileStates.get();
    LoadingScreen.show();
    fileStates
      .openFolderItems(selectedSequenceIds) // TODO: expose through FileBrowserActions
      .then(() => {
        LoadingScreen.hide();
      });
    this.closeModal();
  }

  addFiles(files: Array<File>): void {
    if (files.length > 0) {
      analytics.track('sequence', 'select_files', {
        product: ANALYTICS_MODULE.MOLBIO,
        importFlow: DNA_SEQUENCE_IMPORT_FLOW.FILE,
      });
    }
    const libraryId = this.targetFolderId || '';
    const els = [];
    for (const file of Array.from(files)) {
      const uploadFile = new UploadFile({file});
      this.uploadFiles.add(uploadFile);
      this.uploadQueue.defer(
        (model: UploadFile, callback: () => void) =>
          model.upload(libraryId, this.nucleotideType, this.studySearchItems ?? []).always(() => {
            callback();
          }),
        uploadFile
      );

      els.push(
        new UploadFileView({
          model: uploadFile,
          closeModal: this.closeModal,
        }).render().el
      );
    }

    if (files.length > 0) {
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, libraryId);
    }
    this.$el.find('.uploading-files').prepend(els);
    const $errorsOnlyCheckbox = this.$el.find('.errors-only input');
    if ($errorsOnlyCheckbox.prop('checked')) {
      $errorsOnlyCheckbox.prop('checked', false);
      this._handleShowErrors(false);
    }
  }

  renderUploadFilesHelp(): void {
    this.$el.find('.with-sequences').toggleClass('hidden', this.uploadFiles.length === 0);
  }

  renderSelect(): void {
    const validFiles = this.uploadFiles.where({selectable: true});
    const anySelected = this.uploadFiles.where({anySelected: true, selectable: true}).length > 0;
    const allSelected =
      validFiles.length > 0 &&
      this.uploadFiles.where({allSelected: true, selectable: true}).length === validFiles.length;
    this.$el.find('.import-context-bar .select-all').prop({
      checked: anySelected,
      indeterminate: anySelected && !allSelected,
      disabled: validFiles.length === 0,
    });
    this.$el.find('.import-context-bar .sequence-action').prop('disabled', !anySelected);
    this.$el.find('.import-context-bar .sequence-action').toggleClass('is-disabled', !anySelected);
  }

  renderTagChoices(): void {
    const $menu = this.$el.find('.tag-options');
    const nestedTags: Array<Array<Tag>> = this.uploadFiles.map((file) =>
      file.get('sequences').map((sequence: TagSequence) => sequence.get('tags').models)
    );
    let allTags: Array<Tag> = _.unique(nestedTags.flat(2), (tag: Tag) => tag.get('name').toLowerCase());
    allTags = _.sortBy(allTags, (tag: Tag) => tag.get('name').toLowerCase());
    const els = allTags.map((tag) => {
      const $li = $('<li>');
      const checkbox = $('<input type="checkbox">');
      const $anchor = $('<a>').attr('href', '#').text(tag.get('name')).prepend(checkbox);
      return $li.append($anchor);
    });
    // @ts-expect-error we sometimes use JQuery as htmlString
    $menu.html(els);
  }

  override remove(): Backbone.View<Backbone.Model> {
    DragDropUpload.pop(this.el);
    this.$('.drop-help .glyphicon-question-sign').popover('destroy');
    this.$('[data-toggle="tooltip"]').tooltip('destroy');
    return super.remove();
  }

  override render(): ImportFromFileView {
    // @ts-expect-error we sometimes use JQuery as htmlString
    $('#import').html(this.$el);
    /*
     * Instead of doing two-way data syncing between Backbone and React, we're only updating state from React
     * and syncing that state one way back to Backbone. This way, Backbone doesn't manipulate the parts of the
     * DOM that are updated in React, and vice versa. Because React state never interacts with any of the
     * Backbone-y parts of the DOM, the corresponding parts of the React virtual DOM never update, so React
     * never tries to change those parts of the actual DOM, so it doesn't squash any changes.
     * TODO BNCH-39516: Ideally we will just convert this whole thing to React.
     */
    this.reactRoot = renderToDOM(
      <ImportFromFile
        initialFolderId={this.targetFolderId}
        onMount={() => {
          this.$('[data-toggle="tooltip"]').tooltip();

          const $parent = this.$el.find('.import-context-bar').parent();
          this.$el.find('.import-context-bar').affix({
            offset: {
              top() {
                return $parent.offset()!.top;
              },
            },
          });
          this.tagDropdown = new ImportTagDropdown({
            model: this.uploadFiles,
            el: this.$el.find('.add-tag-menu'),
          });
          this.renderTagChoices();

          if (this.uploadFiles.length > 0) {
            const els = this.uploadFiles.map(
              (uploadFile) =>
                new UploadFileView({
                  model: uploadFile,
                  closeModal: this.closeModal,
                }).render().el
            );
            this.$el.find('.uploading-files').prepend(els);
          }
        }}
        onUpdateNucleotideType={(nucleotideType) => {
          this.nucleotideType = nucleotideType;
        }}
        onUpdateSelectedFolderId={(folderId) => {
          this.targetFolderId = folderId;
        }}
        onUpdateStudySearchItems={(studySearchItems) => {
          this.studySearchItems = studySearchItems;
        }}
      />,
      this.$el[0],
      this.reactRoot
    );
    return this;
  }
}

export default ImportFromFileView;
